import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';

interface SaveBurstProps {
  // Bump to fire a burst (0 = never fired).
  fire: number;
  // CSS colours, e.g. the product photo's own colours.
  colours?: string[];
}

const FALLBACK = ['#22d3ee', '#a855f7', '#f0abfc', '#67e8f9'];

const Burst: React.FC<{ colours: string[] }> = ({ colours }) => {
  const parts = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const angle = (i / 14) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
        const distance = 30 + Math.random() * 30;
        return {
          x: Math.cos(angle) * distance,
          y: Math.sin(angle) * distance,
          size: 4 + Math.random() * 4,
          colour: colours[i % colours.length],
          delay: Math.random() * 0.06,
        };
      }),
    [colours]
  );
  return (
    <>
      <motion.span
        className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ boxShadow: `0 0 0 2px ${colours[0]}` }}
        initial={{ scale: 0.4, opacity: 0.9 }}
        animate={{ scale: 2.2, opacity: 0 }}
        transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
      />
      {parts.map((p, i) => (
        <motion.span
          key={i}
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{ width: p.size, height: p.size, marginLeft: -p.size / 2, marginTop: -p.size / 2, background: p.colour }}
          initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
          animate={{ x: p.x, y: p.y, scale: [0, 1.25, 0], opacity: [1, 1, 0] }}
          transition={{ duration: 0.72, delay: p.delay, ease: [0.2, 0.8, 0.2, 1] }}
        />
      ))}
    </>
  );
};

// Confetti in the garment's own colours, from the middle of the Save button,
// the moment a piece lands in the vault. Skipped under "reduce motion".
const SaveBurst: React.FC<SaveBurstProps> = ({ fire, colours }) => {
  const [bursts, setBursts] = useState<number[]>([]);

  useEffect(() => {
    if (!fire || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    setBursts(b => [...b, fire]);
    const t = setTimeout(() => setBursts(b => b.filter(x => x !== fire)), 1000);
    return () => clearTimeout(t);
  }, [fire]);

  const palette = colours && colours.length ? colours : FALLBACK;
  return (
    <span className="pointer-events-none absolute inset-0 overflow-visible" aria-hidden="true">
      {bursts.map(id => (
        <Burst key={id} colours={palette} />
      ))}
    </span>
  );
};

export default SaveBurst;
